// src/component/GroupActivity2026MyPageSection.js
//
// マイページに差し込むカード：
//  - 現在の回答（参加する／参加しない／未回答）を表示
//  - その場で回答を変更できる（後から気が変わってもOK）
//  - users/{uid}.groupActivity2026 に保存

import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DoDisturbAltRoundedIcon from "@mui/icons-material/DoDisturbAltRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";

import EventRoundedIcon from "@mui/icons-material/EventRounded";

import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { auth, db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";
import {
  GROUP_ACTIVITY_2026_CONFIG_DOC,
  GA2026_AUDIENCE,
} from "./groupActivity2026Defaults";
import { formatDeadlineJa } from "./groupActivity2026Format";
import { ensureServerTimeSynced } from "../api/serverTime";
import { canSeeGroupActivity2026 } from "./groupActivity2026Audience";

export default function GroupActivity2026MyPageSection() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";
  const surface = theme.surfaceBg ?? "#FFFFFF";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";

  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState(null); // "join" | "decline" | null
  const [saving, setSaving] = useState(null); // "join" | "decline" | null
  const [deadline, setDeadline] = useState("");
  const [audience, setAudience] = useState(GA2026_AUDIENCE.ADMIN);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userData, setUserData] = useState(null);
  const [serverTimeReady, setServerTimeReady] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });

  // サーバ時刻同期（formatDeadlineJa が serverNowMs を使うため、判定の前に1回走らせる）
  useEffect(() => {
    let alive = true;
    ensureServerTimeSynced().finally(() => {
      if (alive) setServerTimeReady(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const current = auth.currentUser;
    if (!current) {
      setLoading(false);
      return;
    }
    const ref = doc(db, "users", current.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() || {};
          setResponse(data.groupActivity2026?.response ?? null);
          setIsAdmin(!!data.isAdmin);
          setUserData(data);
        } else {
          setResponse(null);
          setIsAdmin(false);
          setUserData(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error("GroupActivity2026MyPageSection error:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // 期日・公開範囲を systemConfig から取得
  useEffect(() => {
    const ref = doc(db, "systemConfig", GROUP_ACTIVITY_2026_CONFIG_DOC);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setDeadline(data?.deadline || "");
        setAudience(
          data?.audience === GA2026_AUDIENCE.ALL
            ? GA2026_AUDIENCE.ALL
            : GA2026_AUDIENCE.ADMIN
        );
      },
      (err) => {
        console.error("GroupActivity2026MyPageSection config error:", err);
      }
    );
    return () => unsub();
  }, []);

  // serverTimeReady を依存に含めて、同期完了後に再評価されるようにする
  const deadlineInfo = serverTimeReady ? formatDeadlineJa(deadline) : null;
  const isPastDeadline = !!deadlineInfo?.isPast;

  // 公開範囲ゲート：admin → 管理者のみ、all → 4期支払い済みユーザー（管理者は常に可）
  const audienceAllows = canSeeGroupActivity2026({
    audience,
    isAdmin,
    userData,
  });

  // 「自分は見るべきではない」ユーザーには何も描画しない
  if (!audienceAllows) return null;

  const showSnack = (message, type = "success") =>
    setSnackbar({ open: true, message, type });

  const handleSelect = async (value) => {
    const current = auth.currentUser;
    if (!current) return;
    if (saving) return;
    if (isPastDeadline) {
      showSnack("回答期日を過ぎているため変更できません。", "error");
      return;
    }
    setSaving(value);
    try {
      const ref = doc(db, "users", current.uid);
      await setDoc(
        ref,
        {
          groupActivity2026: {
            response: value,
            respondedAt: serverTimestamp(),
          },
        },
        { merge: true }
      );
      showSnack(
        value === "join"
          ? "「参加する」で更新しました。"
          : "「参加しない」で更新しました。"
      );
    } catch (e) {
      console.error("GroupActivity2026MyPageSection save error:", e);
      showSnack("更新に失敗しました。時間をおいて再度お試しください。", "error");
    } finally {
      setSaving(null);
    }
  };

  const statusChip = (() => {
    if (response === "join") {
      return (
        <Chip
          icon={<CheckCircleRoundedIcon />}
          label="現在：参加する"
          sx={{
            bgcolor: `${accent}18`,
            color: accent,
            fontWeight: 800,
            border: `1px solid ${accent}33`,
          }}
        />
      );
    }
    if (response === "decline") {
      return (
        <Chip
          icon={<DoDisturbAltRoundedIcon />}
          label="現在：参加しない"
          sx={{
            bgcolor: "rgba(0,0,0,0.04)",
            color: "rgba(0,0,0,0.7)",
            fontWeight: 800,
            border: "1px solid rgba(0,0,0,0.12)",
          }}
        />
      );
    }
    return (
      <Chip
        icon={<HelpOutlineRoundedIcon />}
        label="現在：未回答"
        sx={{
          bgcolor: "rgba(255,180,0,0.12)",
          color: "rgba(120,80,0,0.95)",
          fontWeight: 800,
          border: "1px solid rgba(255,180,0,0.4)",
        }}
      />
    );
  })();

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3 },
        borderRadius: 3,
        border: `1px solid ${border}`,
        bgcolor: surface,
        boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
        mb: 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            background:
              "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
              `linear-gradient(135deg, ${accent}22, ${accent}11)`,
            boxShadow:
              "0 8px 18px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(255,255,255,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 1.5,
          }}
        >
          <GroupsRoundedIcon sx={{ color: accent }} />
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: textBase }}>
            2026年 グループ活動 参加意思
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.65)" }}>
            {isPastDeadline
              ? "回答期日を過ぎたため、ここからの変更はできません。"
              : "ここから「参加する／参加しない」を変更できます。"}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>{statusChip}</Box>

          {deadlineInfo && (
            <Box
              sx={{
                mb: 2,
                p: 1.2,
                borderRadius: 2,
                bgcolor: deadlineInfo.isPast
                  ? "rgba(220,60,60,0.08)"
                  : `${accent}10`,
                border: `1px solid ${
                  deadlineInfo.isPast
                    ? "rgba(220,60,60,0.3)"
                    : `${accent}33`
                }`,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <EventRoundedIcon
                sx={{
                  fontSize: 18,
                  color: deadlineInfo.isPast ? "rgb(180,40,40)" : accent,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                  color: deadlineInfo.isPast ? "rgb(160,30,30)" : textBase,
                }}
              >
                回答期日：{deadlineInfo.label}
                {deadlineInfo.isPast && "（期日を過ぎています）"}
              </Typography>
            </Box>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
            <Button
              onClick={() => handleSelect("join")}
              disabled={!!saving || isPastDeadline}
              variant={response === "join" ? "contained" : "outlined"}
              startIcon={<CheckCircleRoundedIcon />}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 800,
                px: 2.5,
                ...(response === "join"
                  ? {
                      bgcolor: accent,
                      "&:hover": { bgcolor: accent, filter: "brightness(0.95)" },
                    }
                  : {}),
              }}
            >
              {saving === "join" ? "更新中…" : "参加する"}
            </Button>
            <Button
              onClick={() => handleSelect("decline")}
              disabled={!!saving || isPastDeadline}
              variant={response === "decline" ? "contained" : "outlined"}
              startIcon={<DoDisturbAltRoundedIcon />}
              color="inherit"
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 700,
                px: 2.5,
              }}
            >
              {saving === "decline" ? "更新中…" : "参加しない"}
            </Button>
          </Stack>

        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.type}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
